import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
  SHIPPERS,
  equipmentRequestCompleteSchema,
  getReportDef,
  shipmentCancelSchema,
  shipmentFulfilSchema,
  shipmentScanSchema,
  shipmentShipSchema,
} from '@app/shared';
import type {
  AddressCheckDto,
  DropshipOrderType,
  PendingOrdersByType,
  PendingOrdersQueue,
  PendingShipmentOrderDto,
  ReportRow,
  ShipmentOrderDetailDto,
  Shipper,
} from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { FormDialog } from '../../components/dialogs';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { ReleasedFeature, useFeatureReleased } from '../../release';
import { Can } from '../auth/guards';
import { withRequiredMessages } from '../inventory/formUtils';
import { ReportScreen, reportQueryKey } from '../reports/ReportPage';

const text = (row: ReportRow, key: string): string => {
  const value = row[key];
  return value === null || value === undefined ? '' : String(value);
};

interface ScanForm {
  sim: string;
  shipper: Shipper | '';
}
const scanResolver = withRequiredMessages<ScanForm>(
  zodResolver(shipmentScanSchema) as unknown as Resolver<ScanForm>,
  { sim: 'ESN/SIM is required', shipper: 'Select Shipper' },
);
const SHIPPER_OPTIONS = toOptions(SHIPPERS);

/* ----------------------------- Printable papers ---------------------------- */

const PRINT_CSS = `@media print {
  body * { visibility: hidden !important; }
  .print-paper, .print-paper * { visibility: visible !important; }
  .print-paper { position: fixed; left: 0; top: 0; width: 100%; border: 0 !important; }
}`;

/** A dialog whose content is what the printer gets: everything else is hidden on paper. */
function PrintDialog({
  title,
  open,
  onClose,
  children,
  action,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Another way out of the dialog, beside Print. */
  action?: ReactNode;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <style>{PRINT_CSS}</style>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Paper variant="outlined" className="print-paper" sx={{ p: 2 }}>
          {children}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {action}
        <Button variant="contained" onClick={() => window.print()}>
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const shipTo = (row: ReportRow): string[] =>
  [
    `${text(row, 'firstName')} ${text(row, 'lastName')}`,
    text(row, 'mailing1'),
    text(row, 'mailing2'),
    `${text(row, 'mailingCity')}, ${text(row, 'state')} ${text(row, 'mailingZip')}`,
  ].filter((line) => line.trim() !== '' && line.trim() !== ',');

/**
 * "Print label": the address and tracking number of an order whose label was bought, and a link
 * to the label the shipper made (`GET /shipments/labels/:trackingNumber`; 404 when none was kept).
 */
function LabelDialog({ order, onClose }: { order: ReportRow | null; onClose: () => void }) {
  return (
    <PrintDialog
      title="Print label"
      open={Boolean(order)}
      onClose={onClose}
      action={
        order?.labelKept ? (
          <Button
            href={api.url(`/shipments/labels/${encodeURIComponent(text(order, 'trackingNumber'))}`)}
            target="_blank"
            rel="noopener"
          >
            Open the label
          </Button>
        ) : null
      }
    >
      {order ? (
        <Box aria-label="Shipping label">
          <Typography variant="caption" color="text.secondary">
            {text(order, 'shipper')} · Enrollment {text(order, 'enrollmentId')}
          </Typography>
          <Typography sx={{ fontWeight: 700, mt: 1 }}>SHIP TO</Typography>
          {shipTo(order).map((line) => (
            <Typography key={line}>{line}</Typography>
          ))}
          <Typography sx={{ mt: 1.5, fontFamily: 'monospace', fontSize: 16 }}>
            {text(order, 'trackingNumber')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Address slip. No postage: open the label for the one the shipper made.
          </Typography>
        </Box>
      ) : null}
    </PrintDialog>
  );
}

const SUMMARY_LINES: Array<[string, string]> = [
  ['Enrollment ID', 'enrollmentId'],
  ['Plan', 'planName'],
  ['Requested Model', 'requestedModel'],
  ['SIM', 'sim'],
  ['Device ID', 'deviceId'],
  ['Shipper', 'shipper'],
  ['Tracking Number', 'trackingNumber'],
  ['Master Agent', 'masterAgent'],
];

/** "Generate Order Summary": what is in the parcel and where it goes, to print. */
function SummaryDialog({ order, onClose }: { order: ReportRow | null; onClose: () => void }) {
  return (
    <PrintDialog title="Generate Order Summary" open={Boolean(order)} onClose={onClose}>
      {order ? (
        <Box aria-label="Order summary">
          <Typography sx={{ fontWeight: 700 }}>Order Summary</Typography>
          <Table size="small" sx={{ my: 1 }}>
            <TableBody>
              {SUMMARY_LINES.filter(([, key]) => text(order, key) !== '').map(([label, key]) => (
                <TableRow key={key}>
                  <TableCell sx={{ pl: 0, color: 'text.secondary', width: 150 }}>{label}</TableCell>
                  <TableCell>{text(order, key)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography sx={{ fontWeight: 700 }}>Ship to</Typography>
          {shipTo(order).map((line) => (
            <Typography key={line}>{line}</Typography>
          ))}
        </Box>
      ) : null}
    </PrintDialog>
  );
}

/** The two print buttons of an order row. A label exists once a tracking number does. */
function printColumns(
  onLabel: (row: ReportRow) => void,
  onSummary: (row: ReportRow) => void,
): Column<ReportRow>[] {
  return [
    {
      key: 'printLabel',
      header: 'Print Label',
      render: (row) =>
        text(row, 'trackingNumber') ? (
          <Button
            size="small"
            aria-label={`Print label ${text(row, 'enrollmentId')}`}
            onClick={() => onLabel(row)}
          >
            Print label
          </Button>
        ) : null,
    },
    {
      key: 'orderSummary',
      header: 'Generate Order Summary',
      render: (row) => (
        <Button
          size="small"
          aria-label={`Generate Order Summary ${text(row, 'enrollmentId')}`}
          onClick={() => onSummary(row)}
        >
          Print
        </Button>
      ),
    },
  ];
}

/* ------------------------------ Shipping Queue ----------------------------- */

interface FulfilForm {
  enrollmentId: string;
  sim: string;
  deviceId: string;
}
const fulfilResolver = withRequiredMessages<FulfilForm>(
  zodResolver(shipmentFulfilSchema) as unknown as Resolver<FulfilForm>,
  { enrollmentId: 'Enrollment ID is required', sim: 'SIM is required' },
);

/**
 * "Order Fulfillment" (one by one). Opened from a row it carries that order; opened with the
 * "One by One" button the Enrollment ID is typed. No shipper is asked for here.
 */
function FulfilDialog({
  open,
  order,
  onClose,
}: {
  open: boolean;
  order: ReportRow | null;
  onClose: () => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, setError } = useForm<FulfilForm>({
    defaultValues: { enrollmentId: '', sim: '', deviceId: '' },
    resolver: fulfilResolver,
  });
  useEffect(() => {
    if (open)
      reset({ enrollmentId: order ? text(order, 'enrollmentId') : '', sim: '', deviceId: '' });
  }, [open, order, reset]);
  const fulfil = useMutation({
    mutationFn: (input: FulfilForm) =>
      api.post<{ enrollmentId: string; sim: string }>('/shipments/fulfil', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: reportQueryKey('shipping-queue') }),
  });
  const submit = handleSubmit(async (values) => {
    try {
      const done = await fulfil.mutateAsync(values);
      notify.success(`Order ${done.enrollmentId} fulfilled with SIM ${done.sim}.`);
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  const shown: Array<[string, string]> = [
    ['State', 'state'],
    ['Plan', 'planName'],
    ['Master Agent', 'masterAgent'],
    ['Requested Model', 'requestedModel'],
  ];
  return (
    <FormDialog
      open={open}
      title="Order Fulfillment"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={fulfil.isPending}
      maxWidth="sm"
    >
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextInput
          control={control}
          name="enrollmentId"
          label="Enrollment ID"
          required
          disabled={Boolean(order)}
          autoFocus={!order}
          slotProps={{ htmlInput: { maxLength: 20 } }}
        />
        <TextInput
          control={control}
          name="sim"
          label="SIM"
          required
          autoFocus={Boolean(order)}
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 22, inputMode: 'numeric' } }}
        />
        <TextInput
          control={control}
          name="deviceId"
          label="Device ID"
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 16, inputMode: 'numeric' } }}
        />
        {order
          ? shown.map(([label, key]) => (
              <TextField key={key} label={label} value={text(order, key)} disabled />
            ))
          : null}
      </Box>
    </FormDialog>
  );
}

/** "Scan ESN/SIM for shipment": the shipper is chosen here and the parcel leaves. */
function ScanDialog({ order, onClose }: { order: ReportRow | null; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, setError } = useForm<ScanForm>({
    defaultValues: { sim: '', shipper: '' },
    resolver: scanResolver,
  });
  useEffect(() => {
    // A label made beforehand (Bulk Print Label) already fixed the shipper.
    if (order) reset({ sim: text(order, 'sim'), shipper: text(order, 'shipper') as Shipper | '' });
  }, [order, reset]);
  const scan = useMutation({
    mutationFn: (input: ScanForm) =>
      api.post<{ enrollmentId: string; trackingNumber: string }>('/shipments/scan', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: reportQueryKey('shipping-queue') }),
  });
  const submit = handleSubmit(async (values) => {
    try {
      const done = await scan.mutateAsync(values);
      notify.success(
        done.trackingNumber
          ? `Order ${done.enrollmentId} shipped. Tracking number ${done.trackingNumber}.`
          : `Order ${done.enrollmentId} shipped.`,
      );
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <FormDialog
      open={Boolean(order)}
      title="Scan ESN/SIM for shipment"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={scan.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextInput
          control={control}
          name="sim"
          label="Enter ESN/SIM"
          required
          autoFocus
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 22, inputMode: 'numeric' } }}
        />
        <SelectInput
          control={control}
          name="shipper"
          label="Shipper"
          required
          emptyLabel="Select"
          options={SHIPPER_OPTIONS}
        />
      </Box>
    </FormDialog>
  );
}

function CancelDialog({ order, onClose }: { order: ReportRow | null; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, setError } = useForm<{ reason: string }>({
    defaultValues: { reason: '' },
    resolver: zodResolver(shipmentCancelSchema),
  });
  useEffect(() => {
    if (order) reset({ reason: '' });
  }, [order, reset]);
  const cancel = useMutation({
    mutationFn: (input: { reason: string }) =>
      api.post(`/shipments/${text(order!, 'id')}/cancel`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: reportQueryKey('shipping-queue') }),
  });
  const submit = handleSubmit(async (values) => {
    try {
      await cancel.mutateAsync(values);
      notify.success('Order cancelled.');
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <FormDialog
      open={Boolean(order)}
      title="Cancel Order"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={cancel.isPending}
      submitLabel="Cancel Order"
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        {order && text(order, 'status') === 'Assigned' ? (
          <Alert severity="info">The assigned SIM and device go back to Free stock.</Alert>
        ) : null}
        <TextInput
          control={control}
          name="reason"
          label="Reason"
          required
          autoFocus
          multiline
          minRows={2}
          slotProps={{ htmlInput: { maxLength: 250 } }}
        />
      </Box>
    </FormDialog>
  );
}

/** The switch of View Order Details and USPS Address Request and Response. */
const DETAILS_FEATURE = 'shipping-queue-order-details';

interface ShipForm {
  sim: string;
  deviceId: string;
  shipper: Shipper | '';
}
const shipResolver = withRequiredMessages<ShipForm>(
  zodResolver(shipmentShipSchema) as unknown as Resolver<ShipForm>,
  { shipper: 'Select Shipper' },
);

/** The columns of "View Order Details" that 212Ez holds, in the reference's order. */
const DETAIL_COLUMNS: Array<[string, keyof ShipmentOrderDetailDto]> = [
  ['Status', 'status'],
  ['Requested Model', 'requestedModel'],
  ['SIM', 'sim'],
  ['First Name', 'firstName'],
  ['Last Name', 'lastName'],
  ['Address', 'address'],
  ['City', 'city'],
  ['State', 'state'],
  ['Zip code', 'zip'],
  ['Mailing Add1', 'mailing1'],
  ['Mailing Add2', 'mailing2'],
  ['Mailing City', 'mailingCity'],
  ['Mailing State', 'mailingState'],
  ['Mailing Zipcode', 'mailingZip'],
  ['Order Type', 'orderType'],
  ['Plan Name', 'planName'],
];

/**
 * "View Order Details": the order's line with Attach SIM and Attach DEVICE, the Shipper, and
 * "Submit Bulk Shipment", which gives the SIM and sends the parcel in one go.
 */
function OrderDetailsDialog({ order, onClose }: { order: ReportRow | null; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const id = order ? text(order, 'id') : '';
  const details = useQuery({
    queryKey: ['shipments', id, 'details'],
    queryFn: () => api.get<ShipmentOrderDetailDto>(`/shipments/${id}/details`),
    enabled: Boolean(order),
  });
  const { control, handleSubmit, reset, setError } = useForm<ShipForm>({
    defaultValues: { sim: '', deviceId: '', shipper: '' },
    resolver: shipResolver,
  });
  useEffect(() => {
    if (order) reset({ sim: '', deviceId: '', shipper: '' });
  }, [order, reset]);
  const ship = useMutation({
    mutationFn: (input: ShipForm) =>
      api.post<{ enrollmentId: string; trackingNumber: string }>(`/shipments/${id}/ship`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: reportQueryKey('shipping-queue') }),
  });
  const submit = handleSubmit(async (values) => {
    try {
      const done = await ship.mutateAsync(values);
      notify.success(
        done.trackingNumber
          ? `Order ${done.enrollmentId} shipped. Tracking number ${done.trackingNumber}.`
          : `Order ${done.enrollmentId} shipped.`,
      );
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  const d = details.data;
  const waiting = d?.status === 'New';
  const shippable = d?.status === 'New' || d?.status === 'Assigned';
  return (
    <Dialog open={Boolean(order)} onClose={onClose} maxWidth="xl" fullWidth>
      <style>{PRINT_CSS}</style>
      <DialogTitle>View Order Details</DialogTitle>
      <DialogContent dividers>
        {details.error ? (
          <ErrorState error={details.error} onRetry={() => void details.refetch()} />
        ) : !d ? (
          <LoadingState />
        ) : (
          <Box
            component="form"
            noValidate
            aria-label="View Order Details"
            onSubmit={(e) => void submit(e)}
            sx={{ display: 'grid', gap: 2 }}
          >
            <Box className="print-paper" sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Order lines">
                <TableHead>
                  <TableRow>
                    {['#', 'Enrollment ID', 'Attach SIM', 'Attach DEVICE'].map((h) => (
                      <TableCell key={h} sx={{ whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                    {DETAIL_COLUMNS.map(([h]) => (
                      <TableCell key={h} sx={{ whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Activated Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>1</TableCell>
                    <TableCell>{d.enrollmentId}</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      {waiting ? (
                        <TextInput
                          control={control}
                          name="sim"
                          label="Enter SIM"
                          size="small"
                          autoComplete="off"
                          slotProps={{ htmlInput: { maxLength: 22, inputMode: 'numeric' } }}
                        />
                      ) : (
                        d.sim
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      {waiting ? (
                        <TextInput
                          control={control}
                          name="deviceId"
                          label="Please enter Device ID"
                          size="small"
                          autoComplete="off"
                          slotProps={{ htmlInput: { maxLength: 16, inputMode: 'numeric' } }}
                        />
                      ) : (
                        d.deviceId
                      )}
                    </TableCell>
                    {DETAIL_COLUMNS.map(([h, key]) => (
                      <TableCell key={h} sx={{ whiteSpace: 'nowrap' }}>
                        {String(d[key] ?? '')}
                      </TableCell>
                    ))}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {d.activatedAt ? formatDateTime(d.activatedAt) : ''}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
            {shippable ? (
              <Box sx={{ maxWidth: 260 }}>
                <SelectInput
                  control={control}
                  name="shipper"
                  label="Shipper"
                  required
                  emptyLabel="Select"
                  options={SHIPPER_OPTIONS}
                />
              </Box>
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={() => window.print()} disabled={!d}>
          Print
        </Button>
        {shippable ? (
          <Button variant="contained" disabled={ship.isPending} onClick={() => void submit()}>
            Submit Bulk Shipment
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

/** "USPS Address Request and Response": the address checks of the order's enrollment. */
function AddressChecksDialog({ order, onClose }: { order: ReportRow | null; onClose: () => void }) {
  const id = order ? text(order, 'id') : '';
  const checks = useQuery({
    queryKey: ['shipments', id, 'address-checks'],
    queryFn: () => api.get<{ data: AddressCheckDto[] }>(`/shipments/${id}/address-checks`),
    enabled: Boolean(order),
  });
  const columns: Column<AddressCheckDto>[] = [
    { key: '#', header: '#', width: 40, render: (_c, i) => i + 1 },
    { key: 'api', header: 'Api', nowrap: true, render: (c) => c.api },
    {
      key: 'request',
      header: 'Request',
      render: (c) => <Box sx={{ wordBreak: 'break-all', fontSize: 12 }}>{c.request}</Box>,
    },
    {
      key: 'response',
      header: 'Response',
      render: (c) => <Box sx={{ wordBreak: 'break-all', fontSize: 12 }}>{c.response}</Box>,
    },
    { key: 'at', header: 'Date Time', nowrap: true, render: (c) => formatDateTime(c.at) },
  ];
  return (
    <Dialog open={Boolean(order)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>USPS Address Request and Response</DialogTitle>
      <DialogContent dividers>
        <DataTable
          label="USPS Address Request and Response"
          columns={columns}
          rows={checks.data?.data}
          rowKey={(c) => c.id}
          loading={checks.isFetching}
          error={checks.error}
          onRetry={() => void checks.refetch()}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Shipping Queue (PC1066): orders that wait for a SIM to be sent to the customer. */
export function ShippingQueuePage() {
  const detailsOn = useFeatureReleased()(DETAILS_FEATURE);
  const [viewing, setViewing] = useState<ReportRow | null>(null);
  const [checking, setChecking] = useState<ReportRow | null>(null);
  // `row: null` is the "One by One" button: the Enrollment ID is typed.
  const [fulfilling, setFulfilling] = useState<{ row: ReportRow | null } | null>(null);
  const [scanning, setScanning] = useState<ReportRow | null>(null);
  const [cancelling, setCancelling] = useState<ReportRow | null>(null);
  const [label, setLabel] = useState<ReportRow | null>(null);
  const [summary, setSummary] = useState<ReportRow | null>(null);
  return (
    <>
      <ReportScreen
        def={getReportDef('shipping-queue')!}
        chooserTitle="Shipping report filters"
        headerActions={
          <>
            <Button variant="contained" onClick={() => setFulfilling({ row: null })}>
              One by One
            </Button>
            <Button component={Link} to="/inventory/bulk/order-fulfillment" variant="outlined">
              Bulk Fulfillment
            </Button>
          </>
        }
        extraColumns={printColumns(setLabel, setSummary)}
        rowActions={(row) => {
          const status = text(row, 'status');
          const name = text(row, 'enrollmentId');
          return (
            <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap' }}>
              {detailsOn ? (
                <>
                  <Button
                    size="small"
                    aria-label={`View Order Details ${name}`}
                    onClick={() => setViewing(row)}
                  >
                    View Order Details
                  </Button>
                  <Button
                    size="small"
                    aria-label={`USPS Address Request and Response ${name}`}
                    onClick={() => setChecking(row)}
                  >
                    USPS Address
                  </Button>
                </>
              ) : null}
              {status === 'New' ? (
                <Button
                  size="small"
                  aria-label={`Order Fulfillment ${name}`}
                  onClick={() => setFulfilling({ row })}
                >
                  Order Fulfillment
                </Button>
              ) : null}
              {status === 'Assigned' ? (
                <Button
                  size="small"
                  aria-label={`Scan ESN/SIM for shipment ${name}`}
                  onClick={() => setScanning(row)}
                >
                  Scan for shipment
                </Button>
              ) : null}
              {status === 'New' || status === 'Assigned' ? (
                <Button
                  size="small"
                  color="error"
                  aria-label={`Cancel ${name}`}
                  onClick={() => setCancelling(row)}
                >
                  Cancel
                </Button>
              ) : null}
            </Box>
          );
        }}
      />
      <FulfilDialog
        open={Boolean(fulfilling)}
        order={fulfilling?.row ?? null}
        onClose={() => setFulfilling(null)}
      />
      <ScanDialog order={scanning} onClose={() => setScanning(null)} />
      <CancelDialog order={cancelling} onClose={() => setCancelling(null)} />
      <LabelDialog order={label} onClose={() => setLabel(null)} />
      <SummaryDialog order={summary} onClose={() => setSummary(null)} />
      <OrderDetailsDialog order={viewing} onClose={() => setViewing(null)} />
      <AddressChecksDialog order={checking} onClose={() => setChecking(null)} />
    </>
  );
}

/* ---------------------------- Shipment Dashboard --------------------------- */

/** The scan form above the dashboard: the parcel with this SIM leaves with the chosen shipper. */
function ScanOutForm() {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, setError } = useForm<ScanForm>({
    defaultValues: { sim: '', shipper: '' },
    resolver: scanResolver,
  });
  const scan = useMutation({
    mutationFn: (input: ScanForm) => api.post<{ enrollmentId: string }>('/shipments/scan', input),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: reportQueryKey('shipment-dashboard') }),
  });
  const submit = handleSubmit(async (values) => {
    try {
      const done = await scan.mutateAsync(values);
      notify.success(`Order ${done.enrollmentId} scanned and shipped.`);
      reset({ sim: '', shipper: values.shipper });
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      aria-label="Scan for shipment"
      onSubmit={(e) => void submit(e)}
      sx={{
        p: 1.5,
        mb: 1.5,
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ width: 260 }}>
        <TextInput
          control={control}
          name="sim"
          label="Enter SIM"
          required
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 22, inputMode: 'numeric' } }}
        />
      </Box>
      <Box sx={{ width: 180 }}>
        <SelectInput
          control={control}
          name="shipper"
          label="Shipper"
          required
          emptyLabel="Select"
          options={SHIPPER_OPTIONS}
        />
      </Box>
      <Button type="submit" variant="contained" disabled={scan.isPending} sx={{ mt: 0.25 }}>
        Scan
      </Button>
    </Paper>
  );
}

/** Shipment Dashboard (PC223): orders with a SIM, scanned out here when they leave. */
export function ShipmentDashboardPage() {
  const [label, setLabel] = useState<ReportRow | null>(null);
  const [summary, setSummary] = useState<ReportRow | null>(null);
  return (
    <>
      <ReportScreen
        def={getReportDef('shipment-dashboard')!}
        top={<ScanOutForm />}
        actionsHeader="Activation"
        extraColumns={printColumns(setLabel, setSummary)}
        rowActions={(row) =>
          // A shipped order whose line is not yet active is activated on the customer's status page.
          text(row, 'scanStatus') === 'Scanned' && text(row, 'accountStatus') === 'Dispatched' ? (
            <Can code="PC95">
              <MuiLink component={Link} to={`/customers/${text(row, 'customerRef')}/status`}>
                Activate
              </MuiLink>
            </Can>
          ) : null
        }
      />
      <LabelDialog order={label} onClose={() => setLabel(null)} />
      <SummaryDialog order={summary} onClose={() => setSummary(null)} />
    </>
  );
}

/* ------------------------------ Dropship Orders ---------------------------- */

const ORDER_DETAIL_COLUMNS: Column<PendingShipmentOrderDto>[] = [
  { key: '#', header: '#', width: 50, render: (_r, i) => i + 1 },
  { key: 'enrollmentId', header: 'Enrollment ID', render: (r) => r.enrollmentId },
  { key: 'attachEsn', header: 'Attach ESN', render: (r) => r.attachEsn },
  { key: 'status', header: 'Status', render: (r) => r.status },
  { key: 'firstName', header: 'First Name', render: (r) => r.firstName },
  { key: 'lastName', header: 'Last Name', render: (r) => r.lastName },
  { key: 'address', header: 'Address', render: (r) => r.address },
  { key: 'city', header: 'City', render: (r) => r.city },
  { key: 'state', header: 'State', render: (r) => r.state },
  { key: 'zip', header: 'Zip code', render: (r) => r.zip },
  { key: 'mailing1', header: 'Mailing Add1', render: (r) => r.mailing1 },
  { key: 'mailing2', header: 'Mailing Add2', render: (r) => r.mailing2 },
  { key: 'mailingCity', header: 'Mailing City', render: (r) => r.mailingCity },
  { key: 'mailingState', header: 'Mailing State', render: (r) => r.mailingState },
  { key: 'mailingZip', header: 'Mailing Zipcode', render: (r) => r.mailingZip },
  { key: 'orderType', header: 'Order Type', render: (r) => r.orderType },
  { key: 'planName', header: 'Plan Name', render: (r) => r.planName },
  {
    key: 'activatedAt',
    header: 'Activated Date',
    nowrap: true,
    render: (r) => formatDateTime(r.activatedAt),
  },
];

/**
 * "View Shipment Order Details": the waiting orders behind one count. The reference's port-in
 * and provisioning columns, its Action column and "Submit Bulk Shipment" are not built (no port
 * or provisioning data on a shipment order here; what they do was not seen).
 */
function PendingOrderDetails({
  pick,
  onClose,
}: {
  pick: { orderType: DropshipOrderType; state: string };
  onClose: () => void;
}) {
  const orders = useQuery({
    queryKey: ['shipments', 'pending-orders', pick],
    queryFn: () =>
      api
        .get<{ data: PendingShipmentOrderDto[] }>('/shipments/pending-orders', pick)
        .then((r) => r.data),
  });
  return (
    <Dialog open onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>View Shipment Order Details</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ mb: 1 }}>
          {pick.orderType}, {pick.state}
        </Typography>
        <DataTable
          label="Shipment order details"
          columns={ORDER_DETAIL_COLUMNS}
          rows={orders.data}
          rowKey={(r) => r.id}
          loading={orders.isFetching}
          error={orders.error}
          onRetry={() => void orders.refetch()}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => void orders.refetch()} disabled={orders.isFetching}>
          Refresh
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/** "Pending Orders" by order type: a STATE | COUNT table each; a count opens its orders. */
function PendingByType() {
  const summary = useQuery({
    queryKey: ['shipments', 'pending-by-type'],
    queryFn: () => api.get<{ data: PendingOrdersByType[] }>('/shipments/pending-by-type'),
  });
  const [pick, setPick] = useState<{ orderType: DropshipOrderType; state: string } | null>(null);
  if (summary.error)
    return <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />;
  if (!summary.data) return <LoadingState />;
  return (
    <>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, 320px)' }}>
        {summary.data.data.map((t) => (
          <Paper key={t.orderType} variant="outlined" component="section" aria-label={t.orderType}>
            <Typography variant="h3" component="h2" sx={{ px: 1.5, py: 1 }}>
              {t.orderType}
            </Typography>
            <Table size="small" aria-label={`${t.orderType} pending orders`}>
              <TableHead>
                <TableRow>
                  <TableCell>STATE</TableCell>
                  <TableCell align="right">COUNT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {t.rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2}>No pending order.</TableCell>
                  </TableRow>
                ) : (
                  t.rows.map((r) => (
                    <TableRow key={r.state}>
                      <TableCell>{r.state}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          aria-label={`${r.count} ${t.orderType} orders in ${r.state}`}
                          onClick={() => setPick({ orderType: t.orderType, state: r.state })}
                        >
                          {r.count}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        ))}
      </Box>
      {pick ? <PendingOrderDetails pick={pick} onClose={() => setPick(null)} /> : null}
    </>
  );
}

/** Dropship Orders (PC7), "Pending Orders": waiting orders of each queue by state. */
export function DropshipOrdersPage() {
  const featureOn = useFeatureReleased();
  const byType = featureOn('dropship-order-types');
  const summary = useQuery({
    enabled: !byType,
    queryKey: ['shipments', 'pending-summary'],
    queryFn: () => api.get<{ data: PendingOrdersQueue[] }>('/shipments/pending-summary'),
  });
  return (
    <Box>
      <PageHeader
        title="Dropship Orders"
        code="PC7"
        actions={
          <>
            <ReleasedFeature name="shipper-inventory">
              <Button
                component={Link}
                to="/inventory/dropship-orders/shipper-inventory"
                variant="outlined"
              >
                Manage Shipper Inventory
              </Button>
              <Button component={Link} to="/inventory/bulk/shipper-inventory" variant="outlined">
                Shipper Batch Report
              </Button>
            </ReleasedFeature>
            <Can code="PC1066">
              <Button component={Link} to="/inventory/shipping-queue" variant="outlined">
                Shipping Queue
              </Button>
            </Can>
          </>
        }
      />
      <Typography variant="h3" sx={{ mb: 1 }}>
        Pending Orders
      </Typography>
      {byType ? (
        <PendingByType />
      ) : summary.error ? (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : !summary.data ? (
        <LoadingState />
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, 320px)' }}>
          {summary.data.data.map((q) => (
            <Paper key={q.queue} variant="outlined" component="section" aria-label={q.queue}>
              <Typography variant="h3" component="h2" sx={{ px: 1.5, py: 1 }}>
                {q.queue}
              </Typography>
              <Table size="small" aria-label={`${q.queue} pending orders`}>
                <TableHead>
                  <TableRow>
                    <TableCell>STATE</TableCell>
                    <TableCell align="right">COUNT</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {q.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2}>No pending order.</TableCell>
                    </TableRow>
                  ) : (
                    q.rows.map((r) => (
                      <TableRow key={r.state}>
                        <TableCell>{r.state}</TableCell>
                        <TableCell align="right">{r.count}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}

/* ----------------------- Equipment & Accessories Request ------------------- */

interface CompleteForm {
  shipper: Shipper | '';
  trackingNumber: string;
}

function CompleteDialog({ request, onClose }: { request: ReportRow | null; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, setError } = useForm<CompleteForm>({
    defaultValues: { shipper: '', trackingNumber: '' },
    resolver: withRequiredMessages<CompleteForm>(
      zodResolver(equipmentRequestCompleteSchema) as unknown as Resolver<CompleteForm>,
      { shipper: 'Select Shipper' },
    ),
  });
  useEffect(() => {
    if (request) reset({ shipper: '', trackingNumber: '' });
  }, [request, reset]);
  const complete = useMutation({
    mutationFn: (input: CompleteForm) =>
      api.post(`/equipment-requests/${text(request!, 'id')}/complete`, input),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: reportQueryKey('equipment-requests') }),
  });
  const submit = handleSubmit(async (values) => {
    try {
      await complete.mutateAsync(values);
      notify.success('Order completed.');
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <FormDialog
      open={Boolean(request)}
      title="Complete Order"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={complete.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        {request ? (
          <Typography>
            {text(request, 'equipment')} for {text(request, 'customerName')} (
            {text(request, 'customerId')})
          </Typography>
        ) : null}
        <SelectInput
          control={control}
          name="shipper"
          label="Shipper"
          required
          emptyLabel="Select"
          options={SHIPPER_OPTIONS}
        />
        <TextInput
          control={control}
          name="trackingNumber"
          label="Tracking Number"
          slotProps={{ htmlInput: { maxLength: 40 } }}
        />
      </Box>
    </FormDialog>
  );
}

/** Equipment & Accessories Request (PC538): equipment sold to customers, waiting to be sent. */
export function EquipmentRequestsPage() {
  const [completing, setCompleting] = useState<ReportRow | null>(null);
  return (
    <>
      <ReportScreen
        def={getReportDef('equipment-requests')!}
        rowActions={(row) =>
          text(row, 'status') === 'Pending Order' ? (
            <Button
              size="small"
              aria-label={`Complete order ${text(row, 'equipment')} ${text(row, 'customerId')}`}
              onClick={() => setCompleting(row)}
            >
              Complete Order
            </Button>
          ) : null
        }
      />
      <CompleteDialog request={completing} onClose={() => setCompleting(null)} />
    </>
  );
}
